// Duplicate-patient confirmation dialog (2026-10-28) -- the reception-side
// double-entry guard's UI. Shown by PatientForm when POST /patients/duplicate-check
// finds existing patients that look like the prospective entry (same mobile
// suffix + similar name + similar age). The dialog lists each match's stored
// record beside the new entry's values and asks for explicit confirmation:
// "Go back and edit" (default) or "Add as new patient anyway" (families
// legitimately share mobiles -- the guard warns, it never blocks).
import { formatAgeLabel, type AgeYm } from "@emr/shared/age";
import { computeAgeYm } from "@emr/shared/age";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { TriangleAlert } from "lucide-react";

/** One existing-patient match, as returned by POST /patients/duplicate-check. */
export interface DuplicatePatientMatch {
  id: string;
  clinicUhid: string;
  salutation: string | null;
  name: string;
  phone: string | null;
  altPhone: string | null;
  gender: "Male" | "Female" | "Other" | null;
  dob: string | null;
  age: number | null;
  createdAt: string;
}

/** The prospective entry's values, as typed into the form (for the side-by-side). */
export interface DuplicateEntryValues {
  name: string;
  phone: string;
  altPhone?: string;
  ageYears?: number;
  ageMonths?: number;
  dob?: string;
}

function ageLabelOf(m: { age: number | null; dob: string | null }): string {
  if (typeof m.age === "number") {
    const years = Math.floor(m.age / 12);
    const months = m.age % 12;
    return formatAgeLabel({ years, months } as AgeYm) || "—";
  }
  if (m.dob) {
    const a = computeAgeYm(m.dob);
    if (a) return formatAgeLabel(a) || "—";
  }
  return "—";
}

function entryAgeLabel(v: DuplicateEntryValues): string {
  if (v.dob) {
    const a = computeAgeYm(v.dob);
    if (a) return formatAgeLabel(a);
  }
  if (v.ageYears !== undefined) {
    return formatAgeLabel({ years: v.ageYears, months: v.ageMonths ?? 0 });
  }
  return "—";
}

export function DuplicatePatientDialog({
  open,
  onOpenChange,
  matches,
  entry,
  onConfirmAddAnyway,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  matches: DuplicatePatientMatch[];
  entry: DuplicateEntryValues;
  onConfirmAddAnyway: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-maroon-700 dark:text-amber-500">
            <TriangleAlert size={18} className="text-amber-600" />
            Similar patient already exists
          </DialogTitle>
          <DialogDescription>
            {matches.length === 1
              ? "A patient with this mobile number and a similar name and age is already registered. Please confirm this is a different person before adding."
              : `${matches.length} patients with this mobile number and a similar name and age are already registered. Please confirm this is a different person before adding.`}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Existing patient</TableHead>
                <TableHead>UHID</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Age / Gender</TableHead>
                <TableHead>Registered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {matches.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">
                    {[m.salutation, m.name].filter(Boolean).join(" ")}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{m.clinicUhid}</TableCell>
                  <TableCell>{m.phone ?? "—"}{m.altPhone ? ` / ${m.altPhone}` : ""}</TableCell>
                  <TableCell>{ageLabelOf(m)} / {m.gender?.[0] ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted">{m.createdAt?.slice(0, 10)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-amber-100/40">
                <TableCell className="font-medium">
                  <span className="text-[11px] uppercase tracking-wide text-amber-700">New entry</span>{" "}
                  {entry.name}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted">—</TableCell>
                <TableCell>{entry.phone}{entry.altPhone ? ` / ${entry.altPhone}` : ""}</TableCell>
                <TableCell>{entryAgeLabel(entry)} / —</TableCell>
                <TableCell className="text-xs text-muted">now</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Go back and edit
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              onOpenChange(false);
              onConfirmAddAnyway();
            }}
          >
            Add as new patient anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}